import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { OkAloneService } from '../../../../ok-alone.service';
import { WeatherAlertItem, WeatherAlertsQuery } from '../../../../services/weather-alerts.service';

@Component({
  selector: 'app-weather-alerts',
  templateUrl: './weather-alerts.component.html',
  styleUrls: ['./weather-alerts.component.scss']
})
export class WeatherAlertsComponent implements OnChanges, OnInit {

  @Input() items: WeatherAlertItem[] = [];
  @Input() loading = false;
  @Input() isVisible: boolean = false;
  /** When scope filters are shown: always request all countries. */
  @Input() defaultCountry = 'all';
  /** Default proximity radius in miles when scope filters are shown. */
  @Input() defaultProximity = '500';

  @Output() flyTo = new EventEmitter<{ lng: number; lat: number }>();
  @Output() toggleLayer = new EventEmitter<void>();
  @Output() refresh = new EventEmitter<void>();
  @Output() filtersChanged = new EventEmitter<WeatherAlertsQuery>();
  @Output() listFiltersChanged = new EventEmitter<{ search: string; severity: string; category: string }>();

  search = '';
  filterSeverity = '';
  filterCategory = '';
  /** Scope filter values (used when country/proximity row is uncommented in template). */
  filterCountry = 'all';
  filterProximity = '500';
  filtered: WeatherAlertItem[] = [];

  /** localStorage key for persisted dropdown/filter selections. */
  private static readonly STORAGE_KEY = 'weather_alerts_filters';
  /** Persisted filters reset if untouched for this long (24 hours, in ms). */
  private static readonly STORAGE_TTL_MS = 24 * 60 * 60 * 1000;

  readonly severityOrder: Record<string, number> = {
    Extreme: 0,
    Severe: 1,
    Moderate: 2,
    Minor: 3,
    Unknown: 4
  };
  private readonly uppercaseThreatWords = new Set(['osha', 'fema', 'noaa', 'nws', 'us', 'uk']);

  /** Display order for category filter options. */
  private readonly allCategoryFilters = ['weather', 'civil_unrest', 'heat_zone', 'hazard', 'custom', 'factal'] as const;

  /** Maps enabled threat feeds (source) to filterable categories. */
  private readonly feedToCategories: Record<string, readonly string[]> = {
    weather: ['weather'],
    weather_ca: ['weather'],
    factal: ['factal'],
    osha_heat: ['heat_zone'],
    custom: ['civil_unrest', 'heat_zone', 'hazard', 'custom'],
  };

  constructor(public okService: OkAloneService) {}

  /** Category options for the filter dropdown, limited by account-enabled feeds. */
  get availableCategories(): { value: string; labelKey: string }[] {
    const feeds: string[] | undefined = this.okService.settings?.enabled_threat_feeds;
    let allowed: Set<string>;

    if (!feeds?.length) {
      allowed = new Set(this.allCategoryFilters);
    } else {
      allowed = new Set<string>();
      for (const feed of feeds) {
        for (const category of this.feedToCategories[feed] ?? []) {
          allowed.add(category);
        }
      }
    }

    return this.allCategoryFilters
      .filter(value => allowed.has(value))
      .map(value => ({ value, labelKey: this.categoryLabel(value) }));
  }

  private ensureCategoryFilterValid(): void {
    if (this.filterCategory && !this.availableCategories.some(c => c.value === this.filterCategory)) {
      this.filterCategory = '';
      this.applyFilters();
    }
  }

  ngOnInit(): void {
    this.filterCountry = this.defaultCountry;
    this.filterProximity = this.defaultProximity;

    const restoredProximity = this.restoreFilters();

    if (!this.okService.isDemoAccount() && this.filterProximity === 'all') {
      this.filterProximity = this.defaultProximity;
    }
    this.ensureCategoryFilterValid();

    // If a non-default proximity was restored, push it up so the parent
    // re-fetches threats with the saved scope on refresh.
    if (restoredProximity && this.filterProximity !== this.defaultProximity) {
      this.onScopeChange();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['items']) {
      this.applyFilters();
    }
    if (changes['defaultCountry'] && !changes['defaultCountry'].firstChange) {
      this.filterCountry = this.defaultCountry;
    }
    if (changes['defaultProximity'] && !changes['defaultProximity'].firstChange) {
      this.filterProximity = this.defaultProximity;
    }
  }

  onScopeChange(): void {
    this.persistFilters();
    this.filtersChanged.emit({
      country: 'all',
      workerProximity: this.filterProximity,
    });
  }

  applyFilters(): void {
    const s = this.search.toLowerCase().trim();
    this.filtered = (this.items || [])
      .filter(a => {
        if (this.filterSeverity && a.severity !== this.filterSeverity) return false;
        if (this.filterCategory && a.category !== this.filterCategory) return false;
        if (s) {
          const hay = [a.event, a.areaDesc, a.headline].join(' ').toLowerCase();
          if (!hay.includes(s)) return false;
        }
        return true;
      })
      .sort((a, b) =>
        (this.severityOrder[a.severity] ?? 5) - (this.severityOrder[b.severity] ?? 5)
      );
    this.listFiltersChanged.emit({
      search: s,
      severity: this.filterSeverity,
      category: this.filterCategory
    });
  }

  /**
   * Handler for user-driven search/severity/category changes. Persists the
   * selection (refreshing the 24h TTL) then re-applies the filters. Kept
   * separate from {@link applyFilters} so programmatic re-filtering on data
   * load does not reset the expiry timer.
   */
  onListFilterChange(): void {
    this.persistFilters();
    this.applyFilters();
  }

  /** Saves the current dropdown/search selections with a fresh timestamp. */
  private persistFilters(): void {
    try {
      const payload = {
        savedAt: Date.now(),
        search: this.search,
        filterSeverity: this.filterSeverity,
        filterCategory: this.filterCategory,
        filterCountry: this.filterCountry,
        filterProximity: this.filterProximity,
      };
      localStorage.setItem(WeatherAlertsComponent.STORAGE_KEY, JSON.stringify(payload));
    } catch {
      // Ignore storage errors (e.g. private mode / quota); filters just won't persist.
    }
  }

  /**
   * Restores persisted selections if they were saved within the TTL window.
   * Expired or invalid entries are cleared so defaults apply.
   * @returns true if a proximity value was restored from storage.
   */
  private restoreFilters(): boolean {
    try {
      const raw = localStorage.getItem(WeatherAlertsComponent.STORAGE_KEY);
      if (!raw) {
        return false;
      }
      const saved = JSON.parse(raw);
      const savedAt = Number(saved?.savedAt);
      if (!savedAt || (Date.now() - savedAt) > WeatherAlertsComponent.STORAGE_TTL_MS) {
        localStorage.removeItem(WeatherAlertsComponent.STORAGE_KEY);
        return false;
      }

      if (typeof saved.search === 'string') {
        this.search = saved.search;
      }
      if (typeof saved.filterSeverity === 'string') {
        this.filterSeverity = saved.filterSeverity;
      }
      if (typeof saved.filterCategory === 'string') {
        this.filterCategory = saved.filterCategory;
      }
      if (typeof saved.filterCountry === 'string') {
        this.filterCountry = saved.filterCountry;
      }

      let restoredProximity = false;
      if (typeof saved.filterProximity === 'string' && saved.filterProximity) {
        this.filterProximity = saved.filterProximity;
        restoredProximity = true;
      }
      return restoredProximity;
    } catch {
      return false;
    }
  }

  onView(alert: WeatherAlertItem): void {
    if (alert.centroid.lng !== 0 || alert.centroid.lat !== 0) {
      this.flyTo.emit(alert.centroid);
    }
  }

  severityColor(severity: string): string {
    const map: Record<string, string> = {
      Extreme:  '#8B0000',
      Severe:   '#FF4500',
      Moderate: '#FFA500',
      Minor:    '#FFD700',
      Unknown:  '#808080'
    };
    return map[severity] || '#cccccc';
  }

  severityBadgeStyle(severity: string): { [key: string]: string } {
    return { 'background-color': this.severityColor(severity), 'color': '#fff' };
  }

  severityTitle(alert: WeatherAlertItem): string {
    if (alert.severityInt != null && alert.source === 'factal') {
      return `${alert.severity} (Factal ${alert.severityInt}/5)`;
    }
    return alert.severity;
  }

  categoryLabel(category: string): string {
    const labels: Record<string, string> = {
      weather: 'Weather',
      civil_unrest: 'Civil Unrest',
      heat_zone: 'Heat Zone',
      hazard: 'Hazard',
      custom: 'Custom',
      factal: 'Factal'
    };
    return labels[category] || category;
  }

  categoryColor(category: string): string {
    const map: Record<string, string> = {
      weather: '#1e90ff',
      civil_unrest: '#dc3545',
      heat_zone: '#fd7e14',
      hazard: '#6f42c1',
      custom: '#6c757d',
      factal: '#0d9488'
    };
    return map[category] || '#6c757d';
  }

  categoryBadgeStyle(category: string): { [key: string]: string } {
    return { 'background-color': this.categoryColor(category), 'color': '#fff' };
  }

  formatThreatLabel(value: string): string {
    if (!value) {
      return '';
    }
    return value
      .replace(/[_-]+/g, ' ')
      .trim()
      .split(/\s+/)
      .map(part => {
        const lower = part.toLowerCase();
        if (this.uppercaseThreatWords.has(lower)) {
          return lower.toUpperCase();
        }
        return lower.charAt(0).toUpperCase() + lower.slice(1);
      })
      .join(' ');
  }

  formatDate(iso: string): string {
    if (!iso) return 'N/A';
    try {
      return new Date(iso).toLocaleString([], { dateStyle: 'short', timeStyle: 'short' });
    } catch {
      return iso;
    }
  }
}
